/**
 * Generic Table Manager with Alpine.js
 * Provides search and filtering functionality for any table data
 */

/**
 * Create a table manager instance
 * @param {Object} config - Configuration object
 * @param {Array} config.data - Array of data items to manage
 * @param {Array<string>} config.searchFields - Fields to search in (e.g., ['name', 'email'])
 * @param {Function} config.customFilter - Optional custom filter function
 * @returns {Object} Alpine.js data object
 */
export default function tableManager(config = {}) {
    return {
        search: '',
        searchFields: config.searchFields,
        customFilter: config.customFilter,

        /**
         * Source de données TOUJOURS à jour
         */
        get data() {
            return typeof config.data === 'function' ? config.data() : config.data;
        },

        /**
         * Get filtered data based on search query
         * @returns {Array} Filtered data
         */
        get filteredData() {
            if (!this.search.trim()) {
                return this.data;
            }

            const searchLower = this.search.toLowerCase();

            // Use custom filter if provided
            if (this.customFilter && typeof this.customFilter === 'function') {
                return this.data.filter(item => this.customFilter(item, searchLower));
            }

            // Default multi-field search
            return this.data.filter(item => {
                return this.searchFields.some(field => {
                    const value = this.getNestedValue(item, field);
                    return value && String(value).toLowerCase().includes(searchLower);
                });
            });
        },

        /**
         * Get nested object value by path (e.g., 'user.profile.name')
         * @param {Object} obj - Object to search in
         * @param {string} path - Dot-separated path
         * @returns {*} Value at path or undefined
         */
        getNestedValue(obj, path) {
            return path.split('.').reduce((current, prop) => current?.[prop], obj);
        },

        /**
         * Check if there are results
         * @returns {boolean}
         */
        get hasResults() {
            return this.filteredData.length > 0;
        },

        /**
         * Get total number of items
         * @returns {number}
         */
        get totalItems() {
            return this.data.length;
        },

        /**
         * Get number of filtered items
         * @returns {number}
         */
        get filteredCount() {
            return this.filteredData.length;
        },

        /**
         * Check if search is active
         * @returns {boolean}
         */
        get isSearching() {
            return this.search.trim().length > 0;
        },

        /**
         * Clear search input
         */
        clearSearch() {
            this.search = '';
        },

        /**
         * Update data programmatically
         * @param {Array} newData - New data array
         */
        updateData(newData) {
            this.data = newData;
        }
    };
}
